// src/slice/auth/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import { auth } from "../../firebase";
import api from "../../api/axios";

/* ======================
   TOKEN VALIDATION
====================== */
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

/* ======================
   LOCAL STORAGE HELPERS
====================== */
const tokenFromStorage = localStorage.getItem("token");
const userFromStorage = localStorage.getItem("user");

// Clear stale/expired token on app load
if (!isTokenValid(tokenFromStorage)) {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

const validToken = isTokenValid(tokenFromStorage) ? tokenFromStorage : null;
const validUser = validToken && userFromStorage ? JSON.parse(userFromStorage) : null;

/* ======================
   ASYNC THUNKS
====================== */

// SEND OTP
export const sendOTP = createAsyncThunk(
  "auth/sendOTP",
  async ({ phoneNumber, recaptchaVerifier }, { rejectWithValue }) => {
    try {
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifier
      );
      return {
        verificationId: confirmationResult.verificationId,
        phoneNumber,
      };
    } catch (error) {
      console.error("🔥 Firebase OTP Error:", error);
      if (error.code === "auth/too-many-requests") {
        return rejectWithValue("Too many attempts. Please try later.");
      }
      return rejectWithValue(
        "OTP service temporarily unavailable. Please try again."
      );
    }
  }
);

// VERIFY OTP
export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async ({ verificationId, otpCode, phoneNumber }, { rejectWithValue }) => {
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otpCode);
      const userCredential = await signInWithCredential(auth, credential);
      const firebaseUser = userCredential.user;
      const firebaseToken = await firebaseUser.getIdToken();

      const res = await api.post("/api/auth/checkuser", {
        phoneNumber,
        firebaseUid: firebaseUser.uid,
        firebaseToken,
      });

      return {
        isNewUser: res.data.isNewUser,
        firebaseUid: firebaseUser.uid,
        firebaseToken,
        phoneNumber,
        user: res.data.user || null,
        token: res.data.token || null,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "OTP verification failed"
      );
    }
  }
);

// COMPLETE REGISTRATION
export const completeRegistration = createAsyncThunk(
  "auth/completeRegistration",
  async (
    { name, email, phoneNumber, firebaseUid, firebaseToken },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post("/api/auth/registration", {
        name,
        email,
        phoneNumber,
        firebaseUid,
        firebaseToken,
      });

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));

      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Registration failed"
      );
    }
  }
);

/* ======================
   SLICE
====================== */
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: validUser,
    token: validToken,
    loading: false,
    error: null,
    isSuccess: false,
    otpSent: false,
    otpVerified: false,
    isNewUser: false,
    verificationId: null,
    phoneNumber: null,
    firebaseUid: null,
    firebaseToken: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isSuccess = false;
      state.otpSent = false;
      state.otpVerified = false;
      state.isNewUser = false;
      state.verificationId = null;
      state.phoneNumber = null;
      state.firebaseUid = null;
      state.firebaseToken = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    },
    clearAuthState: (state) => {
      state.error = null;
      state.isSuccess = false;
      state.otpSent = false;
      state.otpVerified = false;
      state.isNewUser = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // SEND OTP
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.otpSent = false;
      })
      .addCase(sendOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.otpSent = true;
        state.verificationId = action.payload.verificationId;
        state.phoneNumber = action.payload.phoneNumber;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // VERIFY OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.otpVerified = true;
        state.isNewUser = action.payload.isNewUser;
        state.firebaseUid = action.payload.firebaseUid;
        state.firebaseToken = action.payload.firebaseToken;
        state.phoneNumber = action.payload.phoneNumber;

        if (!action.payload.isNewUser) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isSuccess = true;
          localStorage.setItem("token", action.payload.token);
          localStorage.setItem("user", JSON.stringify(action.payload.user));
        }
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // COMPLETE REGISTRATION
      .addCase(completeRegistration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeRegistration.fulfilled, (state, action) => {
        state.loading = false;
        state.isSuccess = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.otpSent = false;
        state.otpVerified = false;
        state.isNewUser = false;
        state.verificationId = null;
      })
      .addCase(completeRegistration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearAuthState } = authSlice.actions;
export default authSlice.reducer;